import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import type { KeyboardEvent, ReactNode } from "react";
import { pageMax, pageStackAt } from "@/shell/content-frame";

const metricStacked = {
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  rowGap: tokens.spacingVerticalM,
  "& > *:nth-child(odd)": { borderLeft: "none", paddingLeft: 0 },
};

const useStyles = makeStyles({
  panel: {
    display: "grid",
    gridTemplateColumns: "repeat(var(--metric-cols, 4), minmax(0, 1fr))",
    "& > *": { paddingLeft: tokens.spacingHorizontalL, paddingRight: tokens.spacingHorizontalL },
    "& > *:first-child": { paddingLeft: 0 },
    "& > *:not(:first-child)": { borderLeft: `1px solid ${tokens.colorNeutralStroke2}` },
    "@media (max-width: 960px)": metricStacked,
    [pageMax(pageStackAt)]: metricStacked,
  },
  metric: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
    borderRadius: tokens.borderRadiusSmall,
  },
  interactive: {
    cursor: "pointer",
    outlineStyle: "none",
    "&:hover .metric-value": { textDecorationLine: "underline", textDecorationColor: tokens.colorNeutralStroke1, textUnderlineOffset: "3px" },
    ":active": { opacity: 0.8 },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "2px" },
  },
  label: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  valueRow: { display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: tokens.spacingHorizontalS, minWidth: 0 },
  valueRowInline: { flexWrap: "nowrap" },
  value: {
    fontSize: tokens.fontSizeBase500,
    lineHeight: tokens.lineHeightBase500,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.01em",
    color: tokens.colorNeutralForeground1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  dotted: {
    textDecorationLine: "underline",
    textDecorationStyle: "dotted",
    textDecorationColor: tokens.colorNeutralStrokeAccessible,
    textUnderlineOffset: "4px",
  },
  unit: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, whiteSpace: "nowrap", marginLeft: "2px" },
  delta: { fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", flexShrink: 0 },
  caption: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  inlineCaption: { flex: "1 1 auto", minWidth: 0 },
  viz: { paddingTop: tokens.spacingVerticalXS, paddingBottom: "2px" },
  danger: { color: tokens.colorPaletteRedForeground1 },
  warning: { color: tokens.colorPaletteDarkOrangeForeground1 },
  success: { color: tokens.colorPaletteGreenForeground1 },
  muted: { color: tokens.colorNeutralForeground3 },
  neutral: { color: tokens.colorNeutralForeground3 },
  /** 보고서처럼 표와 나란히 둘 때. 20px로 띄우지 않고 본문(13px)과 맞춘다. */
  compactValue: { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, letterSpacing: "0" },
});

export type MetricTone = "danger" | "warning" | "success" | "muted";
/** 델타의 의미: good(초록)·bad(빨강)·neutral(회색). 부호가 아니라 결과가 좋은지로 정한다. */
export type DeltaSense = "good" | "bad" | "neutral";

export function deltaSense(diff: number, higherIsBetter = true): DeltaSense {
  if (diff === 0) return "neutral";
  return diff > 0 === higherIsBetter ? "good" : "bad";
}

/** 질문(진척·일정·리스크·자원) 단위로 지표를 한 줄에 묶는 패널. 칸 사이는 1px 세로 구분선만 쓴다. */
export function MetricPanel({ columns = 4, className, children, ariaLabel }: { columns?: number; className?: string; children: ReactNode; ariaLabel?: string }) {
  const st = useStyles();
  return (
    <div className={mergeClasses(st.panel, className)} style={{ ["--metric-cols" as string]: columns }} role="group" aria-label={ariaLabel}>
      {children}
    </div>
  );
}

export interface MetricProps {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  tone?: MetricTone;
  delta?: { text: string; sense: DeltaSense; title?: string };
  /** 수치 아래 추이(@/components/visual/charts의 SparkVisual). 장식용 게이지는 넣지 않는다. */
  viz?: ReactNode;
  caption?: ReactNode;
  /** 캡션을 수치 줄에 붙여 칸을 두 줄로 유지한다 */
  captionInline?: boolean;
  caption2?: ReactNode;
  /** 본문과 같은 13px. 화면의 주 숫자가 아닐 때 */
  compact?: boolean;
  /** 값 아래 점선. */
  dotted?: boolean;
  onClick?: () => void;
  ariaLabel?: string;
}

export function Metric({ label, value, unit, tone, delta, viz, caption, captionInline, caption2, compact, dotted, onClick, ariaLabel }: MetricProps) {
  const st = useStyles();
  const onKeyDown = onClick
    ? (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }
    : undefined;
  return (
    <div
      className={mergeClasses(st.metric, onClick && st.interactive)}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onKeyDown}
      aria-label={ariaLabel ?? (typeof label === "string" && (typeof value === "string" || typeof value === "number") ? `${label}: ${value}` : undefined)}
    >
      <span className={st.label}>{label}</span>
      <div className={mergeClasses(st.valueRow, captionInline && st.valueRowInline)}>
        <span className={mergeClasses("metric-value", st.value, compact && st.compactValue, dotted && st.dotted, tone && st[tone])}>
          {value}
          {unit !== undefined && <span className={st.unit}>{unit}</span>}
        </span>
        {delta && (
          <span className={mergeClasses(st.delta, delta.sense === "good" ? st.success : delta.sense === "bad" ? st.danger : st.neutral)} title={delta.title}>
            {delta.text}
          </span>
        )}
        {captionInline && caption && <span className={mergeClasses(st.caption, st.inlineCaption)}>{caption}</span>}
      </div>
      {viz && <div className={st.viz}>{viz}</div>}
      {!captionInline && caption && <span className={st.caption}>{caption}</span>}
      {caption2 && <span className={st.caption}>{caption2}</span>}
    </div>
  );
}
